<template>
  <div :class="loading ? 'd-flex' : ''">
    <div class="pt-2" v-show="!loading">
      <template v-if="erro">
        <div class="na-ext-error">
          <font-awesome-icon icon="fa fa-triangle-exclamation" />
          <p class="mb-0 mt-2">{{ erro }}</p>
          <button type="button" class="na-ext-retry" @click="recarregar">Tentar novamente</button>
        </div>
      </template>

      <template v-else-if="detalhe">
        <!-- Identificação da estação (equivale ao seletor de sensor do device próprio) -->
        <div class="na-ext-source">
          <span class="na-label">Estação pública monitorada</span>
          <div class="na-ext-source-box mt-1">
            <div class="na-ext-source-main">
              <span class="na-ext-source-name">{{ detalhe.fonteLabel }}</span>
              <span class="na-ext-source-code">{{ detalhe.codigo }}</span>
            </div>
            <span class="na-ext-source-river" v-if="detalhe.rio">Rio {{ detalhe.rio }}</span>
          </div>
        </div>

        <div class="na-historico-header">
          <span class="na-historico-title">Histórico de leitura</span>
        </div>

        <!-- Acordeão de dias, mesmo padrão do aside de dispositivo -->
        <div class="na-days-container" v-if="days.length">
          <div class="na-days-row">
            <button
              v-for="(d, i) in days"
              :key="d.date"
              :class="['na-day-item', { active: expandedDayIndex === i }]"
              @click="onDaySelect(i)"
            >
              <div class="na-day-label">
                <span class="na-day-text">{{ dayLabel(d.date) }}</span>
              </div>

              <!-- Hoje: valor atual em destaque, com a faixa do dia abaixo.
                   Dias passados: só mín/máx — a "última leitura" de um dia encerrado não
                   diz nada, enquanto os extremos resumem o que aconteceu. -->
              <template v-if="i === days.length - 1">
                <div class="na-day-rain">
                  <span class="na-day-value">{{ dayMainValue(d) }}</span>
                  <span class="na-day-unit">{{ mainUnit }}</span>
                </div>
                <div class="na-day-temp">
                  <span class="na-day-temp-range">{{ dayMainRange(d) }}</span>
                </div>
              </template>

              <template v-else>
                <div class="na-day-minmax">
                  <span class="na-day-minmax-item">
                    <span class="na-day-minmax-cap">máx</span>
                    <span class="na-day-minmax-val">{{ dayMainMax(d) }}</span>
                  </span>
                  <span class="na-day-minmax-item">
                    <span class="na-day-minmax-cap">mín</span>
                    <span class="na-day-minmax-val">{{ dayMainMin(d) }}</span>
                  </span>
                  <span class="na-day-minmax-unit">{{ mainUnit }}</span>
                </div>
              </template>
            </button>
          </div>

          <div v-if="expandedDay" class="na-expanded-content" :style="expandedContentRadius">
            <!-- Última leitura do dia -->
            <div class="na-section na-leitura">
              <div class="na-leitura-row">
                <span class="na-leitura-label">{{ lastReadingIsExact ? "Última leitura" : "Último bloco" }}</span>
                <span class="na-leitura-date">{{ formatDate(lastHourOfDay) }}</span>
                <span class="na-leitura-time">{{ formatTime(lastHourOfDay) }}</span>
              </div>
            </div>

            <!-- Situação de cheia + níveis de referência: são leitura do sensor de nível,
                 então ficam junto com os dados do dia, não no cabeçalho da estação.
                 Só o dia mais recente reflete a situação atual — nos dias anteriores a
                 faixa é omitida para não sugerir que aquele status é de hoje. -->
            <template v-if="isDiaAtual">
              <div class="na-ext-status" :style="{ background: statusColor }">
                <div class="na-ext-status-main">
                  <span class="na-ext-status-label">{{ statusLabel }}</span>
                  <!-- v-html: o ícone é o mesmo SVG do badge do pin, montado no script a
                       partir de uma tabela fixa de paths — nada aqui vem da API. -->
                  <span v-if="tendenciaIcone" class="na-ext-status-trend">
                    <span class="na-ext-trend-glyph" v-html="tendenciaIcone"></span>
                    {{ tendenciaLabel }}
                  </span>
                </div>
                <div class="na-ext-status-foot" v-if="taxaTexto || tendenciaHistorico.length">
                  <span class="na-ext-status-rate">{{ taxaTexto }}</span>
                  <span v-if="tendenciaHistorico.length" class="na-ext-status-form">
                    <span
                      v-for="p in tendenciaHistorico"
                      :key="p.key"
                      class="na-ext-form-dot"
                      :class="{ 'is-current': p.atual }"
                      :style="{ background: p.cor }"
                      :title="p.titulo"
                      v-html="p.icone"
                    ></span>
                  </span>
                </div>
              </div>
            </template>

            <div class="na-section na-ext-refs" v-if="referencias.length">
              <div class="na-ext-refs-label">Níveis de referência</div>
              <div
                class="na-ext-ref-item"
                :class="{ 'na-ext-ref-faixa-atual': ref.atingido && !ref.semvalor }"
                v-for="ref in referencias"
                :key="ref.chave"
              >
                <span class="na-ext-ref-dot" :style="{ background: ref.cor }"></span>
                <span class="na-ext-ref-nome">{{ ref.label }}</span>
                <span class="na-ext-ref-valor" :class="{ atingido: ref.atingido }">{{ ref.valorTexto }}</span>
              </div>
            </div>

            <!-- Cards das grandezas do dia; clicar abre o gráfico logo abaixo da linha -->
            <template v-for="(row, rowIdx) in metricRows" :key="rowIdx">
              <div class="na-metrics-grid">
                <NewAsideMetricCard
                  v-for="card in row"
                  :key="card.grandeza"
                  :label="card.label"
                  :value="card.value"
                  :unit="card.unit"
                  :fa-icon="card.icon"
                  :metric-key="card.grandeza"
                  :selected="selectedGrandeza === card.grandeza"
                  @select="selectCard(card.grandeza)"
                />
              </div>

              <div v-if="isRowSelected(row)" class="na-chart-table-card">
                <NewAsideHistoryChart
                  :hours="chartHours"
                  metric-field="avg"
                  :show-min-max="true"
                  :metric-label="selectedCardLabel"
                  :metric-color="selectedCardColor"
                  :reference-lines="linhasReferencia"
                  :collapsed="historyCollapsed"
                  @toggle-collapse="historyCollapsed = !historyCollapsed"
                />
                <!-- Mesma tabela do aside de dispositivo: herda formato de hora, cabeçalho
                     colapsável e estilo, em vez de reimplementar. -->
                <NewAsideDataTable :hours="chartHours" :columns="tableColumns" pending-label />
              </div>
            </template>
          </div>
        </div>

        <div v-else class="na-ext-empty">Sem leituras no período.</div>

        <div class="na-report-container">
          <a :href="alertLink" class="na-btn-alert">cadastrar alerta</a>
        </div>

        <p class="na-ext-fonte-nota">
          Dados fornecidos por {{ detalhe.fonteLabel }}. O OnAgro apenas exibe as leituras da fonte.
        </p>
      </template>
    </div>

    <div v-if="loading" class="text-center m-auto p-5">
      <Load color="#006035" />
    </div>
  </div>
</template>

<script>
import NewAsideMetricCard from "./NewAsideMetricCard.vue";
import NewAsideHistoryChart from "./NewAsideHistoryChart.vue";
import NewAsideDataTable from "./NewAsideDataTable.vue";
import Load from "../Load.vue";

const WEEK_ABBR = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// Faixas de cheia. As cores anteriores (amarelo/laranja/vermelho-escuro) eram próximas
// demais para distinguir num pin de 26px, então aqui elas ficam bem separadas no
// espectro: verde → amarelo → laranja → magenta. O magenta no lugar do vermelho-escuro
// é intencional: contra o laranja ele se diferencia muito melhor que outro tom de vermelho.
const NIVEL_CORES = {
  normal: "#1e9e4a",
  atencao: "#f2c200",
  alerta: "#f57c00",
  inundacao: "#d81b60",
  semReferencia: "#9e9e9e",
};

// Bolinhas de tendência: mesma anatomia do badge do pin (getExternalTrend no Dashboard)
// — FUNDO pintado com a cor da tendência, seta branca por cima, aro branco em volta.
// As cores são exatamente as do pin: subindo = risco, descendo = alívio, estável = neutro.
//
// Elas NÃO usam NIVEL_CORES (cor da cheia): a faixa atrás já é pintada com ela, e repetir
// a mesma cor apagaria o indicador (verde sobre verde). O aro branco é o que garante que
// o disco se destaque de qualquer fundo de faixa — verde, amarelo, laranja ou magenta.
// Setas idênticas às do badge do pin (getExternalTrendSvg no Dashboard): mesma diagonal
// ↗/↘ e mesmo traço do estável. Antes o card usava glifos Unicode (▲▼＝) e o mapa usava
// SVG, então o MESMO dado aparecia com dois desenhos diferentes conforme a tela.
// `stroke="currentColor"` (o pin fixa #fff) é o que deixa cada bolinha herdar a cor da
// sua tendência; no cabeçalho da faixa o currentColor resolve para o branco do texto.
const TENDENCIA_SVG = {
  subindo: '<path d="M7 17L17 7"/><path d="M9 7h8v8"/>',
  descendo: '<path d="M7 7l10 10"/><path d="M17 9v8H9"/>',
  estavel: '<path d="M6 12h12"/>',
  desconhecida: '<path d="M12 12h.01"/>',
};

function tendenciaSvg(tipo, tamanho) {
  const inner = TENDENCIA_SVG[tipo] || TENDENCIA_SVG.desconhecida;
  return (
    '<svg viewBox="0 0 24 24" width="' + tamanho + '" height="' + tamanho + '" fill="none" ' +
    'stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" ' +
    'aria-hidden="true">' + inner + "</svg>"
  );
}

// Cores idênticas às de getExternalTrend (Dashboard) — mesmo dado, mesma cor nas duas telas.
const TENDENCIA_META = {
  subindo: { rotulo: "Subindo", cor: "#e63946" },
  descendo: { rotulo: "Descendo", cor: "#5c9c3e" },
  estavel: { rotulo: "Estável", cor: "#1e90ff" },
  desconhecida: { rotulo: "Sem leitura", cor: "#9e9e9e" },
};

const NIVEL_LABELS = {
  normal: "Normal",
  atencao: "Atenção",
  alerta: "Alerta",
  inundacao: "Inundação",
};

// Rótulo, unidade, ícone e cor de gráfico por grandeza crua da origem. Uma fonte que
// passe a mandar grandeza nova cai no fallback (rótulo humanizado, sem ícone).
const GRANDEZAS = {
  // Nível sempre em metros — o backend já converte os valores que a origem manda em cm.
  nivel_altitude_m: { label: "Nível\n(altitude)", unit: "m", icon: "fa fa-water", color: "#3E97FF" },
  nivel_cm: { label: "Nível", unit: "m", icon: "fa fa-water", color: "#3E97FF" },
  cota_cm: { label: "Nível\n(cota)", unit: "m", icon: "fa fa-water", color: "#3E97FF" },
  vazao_m3s: { label: "Vazão", unit: "m³/s", icon: "fa fa-gauge-high", color: "#0105F9" },
  chuva_mm: { label: "Chuva", unit: "mm", icon: "fa fa-cloud-showers-heavy", color: "#3B82F6" },
  chuva_1h_mm: { label: "Chuva\n(1h)", unit: "mm", icon: "fa fa-cloud-showers-heavy", color: "#3B82F6" },
  chuva_24h_mm: { label: "Chuva\n(24h)", unit: "mm", icon: "fa fa-cloud-rain", color: "#3B82F6" },
  temperatura_c: { label: "Temperatura", unit: "°C", icon: "fa fa-thermometer-full", color: "#F49C18" },
  umidade_pct: { label: "Umidade", unit: "%", icon: "fa fa-tint", color: "#3E97FF" },
  pressao_hpa: { label: "Pressão", unit: "hPa", icon: "fa fa-gauge", color: "#FF3E3E" },
  vento_vel_ms: { label: "Vento", unit: "m/s", icon: "fa fa-wind", color: "#727272" },
  vento_dir_graus: { label: "Direção\ndo vento", unit: "°", icon: "fa fa-compass", color: "#839682" },
};

export default {
  name: "NewAsideExternalDevice",
  components: { NewAsideMetricCard, NewAsideHistoryChart, NewAsideDataTable, Load },
  data() {
    return {
      ponto: null,
      detalhe: null,
      horario: null,
      dias: 5,
      expandedDayIndex: null,
      selectedGrandeza: null,
      historyCollapsed: false,
      loading: false,
      erro: null,
    };
  },
  computed: {
    resumo() {
      return this.detalhe ? this.detalhe.resumo : null;
    },

    days() {
      return (this.horario && this.horario.days) || [];
    },

    expandedDay() {
      if (this.expandedDayIndex === null) return null;
      return this.days[this.expandedDayIndex] || null;
    },

    /// O resumo ao vivo (status de cheia, tendência, taxa) descreve AGORA — só faz sentido
    /// exibi-lo no dia mais recente, que é o dia a que ele se refere.
    isDiaAtual() {
      return this.days.length > 0 && this.expandedDayIndex === this.days.length - 1;
    },

    /// Bloco "atual" do /horario indexado por grandeza: { temperatura_c: 13.26, ... }.
    /// É a última leitura reportada pela estação.
    valoresAtuais() {
      const mapa = {};
      const atual = (this.horario && this.horario.atual) || [];
      atual.forEach((item) => {
        if (item && item.grandeza != null && item.valor != null) {
          mapa[item.grandeza] = item.valor;
        }
      });
      return mapa;
    },

    /// Grandeza principal da estação (nível, na prática) — define o valor das abas de dia.
    mainGrandeza() {
      if (this.horario && this.horario.grandezaPrincipal) return this.horario.grandezaPrincipal;
      const primeira = this.days.find((d) => Object.keys(d.resumo || {}).length);
      return primeira ? Object.keys(primeira.resumo)[0] : null;
    },

    mainUnit() {
      const info = GRANDEZAS[this.mainGrandeza];
      return info ? info.unit : "";
    },

    /// Cards em linhas de 2, como o grid do aside de dispositivo.
    metricRows() {
      const dia = this.expandedDay;
      if (!dia) return [];

      const cards = Object.keys(dia.resumo || {}).map((grandeza) => {
        const info = GRANDEZAS[grandeza] || { label: this.humanize(grandeza), unit: "", icon: null };
        const stat = dia.resumo[grandeza];
        return {
          grandeza,
          label: info.label,
          unit: info.unit,
          icon: info.icon,
          // No dia de hoje o card mostra a LEITURA ATUAL (como no aside de dispositivo
          // próprio), não a média do dia — a média achata o valor e chega a divergir
          // vários graus do que a estação está medindo agora. Em dias anteriores não
          // existe "atual", então cai para a média daquele dia.
          value: this.fmt(this.valorDoCard(grandeza, stat)),
        };
      });

      // Grandeza principal primeiro; o resto na ordem em que a fonte entregou.
      cards.sort((a, b) => {
        if (a.grandeza === this.mainGrandeza) return -1;
        if (b.grandeza === this.mainGrandeza) return 1;
        return 0;
      });

      const rows = [];
      for (let i = 0; i < cards.length; i += 2) rows.push(cards.slice(i, i + 2));
      return rows;
    },

    /// Horas do dia expandido para a grandeza selecionada, no formato que o
    /// NewAsideHistoryChart espera ({dateTime, avg, min, max}).
    chartHours() {
      const dia = this.expandedDay;
      if (!dia || !this.selectedGrandeza) return [];

      return (dia.hours || [])
        .filter((h) => h.valores && h.valores[this.selectedGrandeza])
        .map((h) => {
          const v = h.valores[this.selectedGrandeza];
          return {
            dateTime: h.dateTime,
            // 'hour' (e não 'hora') é o nome que NewAsideDataTable procura para saber
            // quais horas do dia faltam e marcá-las como offline.
            hour: h.hora,
            avg: v.media,
            min: v.min,
            max: v.max,
          };
        });
    },

    selectedCardLabel() {
      const info = GRANDEZAS[this.selectedGrandeza];
      return info ? info.label.replace("\n", " ") : this.humanize(this.selectedGrandeza);
    },

    selectedCardColor() {
      const info = GRANDEZAS[this.selectedGrandeza];
      return info ? info.color : "#006035";
    },

    /// Colunas da tabela de histórico. 'time' formata a hora a partir de row.dateTime,
    /// gerando "01:00" — o mesmo formato do aside de dispositivo próprio.
    tableColumns() {
      return [
        { key: "hora", label: "hora", format: "time", width: "22%" },
        { key: "min", label: "mínima", formatter: (row) => this.fmt(row.min) },
        { key: "avg", label: "média", formatter: (row) => this.fmt(row.avg) },
        { key: "max", label: "máxima", formatter: (row) => this.fmt(row.max) },
      ];
    },

    // A origem entrega os dados em BLOCOS HORÁRIOS ("2026-08-27T20"), sem minutos — o
    // dateTime de cada hora é montado com ":00:00" fixo no ExternalHourlyMapper. Usá-lo aqui
    // mostrava sempre um horário redondo ("20:00"), que não é a hora da leitura.
    // `ultimaLeituraEm` é o instante real da MEDIÇÃO mais recente. Não usar `ultimaColetaEm`:
    // ele é quando o nosso coletor consultou a fonte, e fontes como a ANA publicam com 1–3 h
    // de atraso — o card dizia "16:45" com o dado mais novo sendo das 14:00, enquanto a tabela
    // (corretamente) mostrava as horas seguintes aguardando. Só vale para o dia da medição;
    // nos demais o bloco horário é o melhor que a fonte oferece.
    lastHourOfDay() {
      const dia = this.expandedDay;
      if (!dia || !dia.hours || !dia.hours.length) return null;

      const ultimoBloco = dia.hours[dia.hours.length - 1].dateTime;

      if (this.ultimaLeituraDoDia) return this.ultimaLeituraDoDia;

      return ultimoBloco;
    },

    ultimaLeituraDoDia() {
      const dia = this.expandedDay;
      const leitura = this.detalhe && this.detalhe.ultimaLeituraEm;
      if (!dia || !leitura) return null;
      return String(leitura).substring(0, 10) === dia.date ? leitura : null;
    },

    // Sem a medição exata do dia só há precisão de hora (ver lastHourOfDay), então o rótulo
    // muda de "Última leitura" para "Último bloco" em vez de fingir uma precisão que não existe.
    lastReadingIsExact() {
      return Boolean(this.ultimaLeituraDoDia);
    },

    /// Casa os cantos do painel com a posição da aba de dia ativa (mesmo truque do device).
    expandedContentRadius() {
      const isFirst = this.expandedDayIndex === 0;
      const isLast = this.expandedDayIndex === this.days.length - 1;
      return { borderRadius: `${isFirst ? "0" : "10px"} ${isLast ? "0" : "10px"} 10px 10px` };
    },

    statusColor() {
      switch ((this.resumo && this.resumo.statusCheia || "").toLowerCase()) {
        case "inundacao": return NIVEL_CORES.inundacao;
        case "alerta": return NIVEL_CORES.alerta;
        case "atencao": return NIVEL_CORES.atencao;
        case "normal": return NIVEL_CORES.normal;
        default: return NIVEL_CORES.semReferencia;
      }
    },

    statusLabel() {
      switch ((this.resumo && this.resumo.statusCheia || "").toLowerCase()) {
        case "inundacao": return "Inundação";
        case "alerta": return "Alerta";
        case "atencao": return "Atenção";
        case "normal": return "Normal";
        default: return "Sem referência de cheia";
      }
    },

    // Mesma seta do badge do pin, herdando o branco do cabeçalho da faixa via currentColor.
    tendenciaIcone() {
      const t = (this.resumo && this.resumo.nivelTendencia || "").toLowerCase();
      if (t !== "subindo" && t !== "descendo" && t !== "estavel") return "";
      return tendenciaSvg(t, 12);
    },

    tendenciaLabel() {
      const t = (this.resumo && this.resumo.nivelTendencia) || "";
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
    },

    // Últimas 3 tendências, da mais antiga para a mais recente — a mesma leitura de
    // "forma recente" das tabelas de campeonato. Três setas para cima seguidas contam
    // uma história que o status atual sozinho não conta.
    tendenciaHistorico() {
      const hist = (this.resumo && this.resumo.nivelTendenciaHistorico) || [];
      if (!hist.length) return [];
      return hist.slice(-3).map((t, i, arr) => {
        const chave = (t || "").toLowerCase();
        const conhecida = Object.prototype.hasOwnProperty.call(TENDENCIA_META, chave)
          && chave !== "desconhecida";
        const meta = conhecida ? TENDENCIA_META[chave] : TENDENCIA_META.desconhecida;
        return {
          key: `${i}-${chave}`,
          cor: meta.cor,
          icone: tendenciaSvg(conhecida ? chave : "desconhecida", 9),
          // A última bolinha é a leitura atual; destacá-la evita confundir a ordem.
          atual: i === arr.length - 1,
          titulo: i === arr.length - 1 ? `Agora: ${meta.rotulo}` : meta.rotulo,
        };
      });
    },

    taxaTexto() {
      if (!this.resumo || this.resumo.nivelTaxaHora == null) return "";
      const unidade = this.resumo.nivelUnidadeOriginal || "";
      const taxa = Number(this.resumo.nivelTaxaHora);
      return `${taxa > 0 ? "+" : ""}${this.fmt(taxa)} ${unidade}/h`;
    },

    /// Faixas de cheia. Sempre renderiza a tabela: quando a fonte não informa referências
    /// (ex.: TideSat), mostra uma linha cinza explicando — antes a seção sumia e dava a
    /// impressão de que o dado estava faltando.
    referencias() {
      if (!this.detalhe) return [];

      const niveis = this.detalhe.niveisReferenciaCheia || {};
      const atual = this.resumo ? this.resumo.nivelOriginal : null;
      const unidade = (this.resumo && this.resumo.nivelUnidadeOriginal) || "";

      const definidas = ["atencao", "alerta", "inundacao"].filter((k) => niveis[k] != null);

      if (definidas.length === 0) {
        return [
          {
            chave: "sem-referencia",
            label: "Sem referência informada",
            cor: NIVEL_CORES.semReferencia,
            valorTexto: "",
            atingido: false,
            semvalor: true,
          },
        ];
      }

      // "Normal" é a faixa abaixo do primeiro limite — a fonte não a informa, mas sem ela
      // não há como saber que o rio está em nível seguro só olhando a tabela.
      const menor = Math.min(...definidas.map((k) => Number(niveis[k])));

      const lista = [
        {
          chave: "normal",
          label: "Normal",
          cor: NIVEL_CORES.normal,
          valorTexto: `abaixo de ${this.fmt(menor)} ${unidade}`.trim(),
          atingido: atual != null && Number(atual) < menor,
        },
      ];

      definidas.forEach((k) => {
        const valor = Number(niveis[k]);
        lista.push({
          chave: k,
          label: NIVEL_LABELS[k] || k,
          cor: NIVEL_CORES[k] || NIVEL_CORES.semReferencia,
          valorTexto: `${this.fmt(valor)} ${unidade}`.trim(),
          atingido: atual != null && Number(atual) >= valor,
        });
      });

      return lista;
    },

    /// Linhas horizontais desenhadas no gráfico — só na série de nível, onde as faixas
    /// de cheia fazem sentido (não em chuva/temperatura/vento).
    linhasReferencia() {
      if (!this.detalhe || !this.ehSerieDeNivel) return [];

      const niveis = this.detalhe.niveisReferenciaCheia || {};

      return ["atencao", "alerta", "inundacao"]
        .filter((k) => niveis[k] != null)
        .map((k) => ({
          value: Number(niveis[k]),
          color: NIVEL_CORES[k],
          label: NIVEL_LABELS[k],
        }));
    },

    /// A grandeza selecionada é nível? (as referências de cheia só valem para ela)
    ehSerieDeNivel() {
      return ["nivel_altitude_m", "nivel_cm", "cota_cm"].includes(this.selectedGrandeza);
    },

    alertLink() {
      if (!this.detalhe) return "/alert";
      const params = new URLSearchParams();
      params.append("externalDeviceId", this.detalhe.id);
      params.append("externalId", this.detalhe.externalId);
      if (this.detalhe.fonte) params.append("fonte", this.detalhe.fonte);
      if (this.detalhe.nome) params.append("nomeDispositivo", this.detalhe.nome);
      return `/alert?${params.toString()}`;
    },
  },
  methods: {
    // Chamado pelo Dashboard ao clicar no pin.
    load(ponto) {
      this.ponto = ponto;
      this.detalhe = null;
      this.horario = null;
      this.expandedDayIndex = null;
      this.selectedGrandeza = null;
      this.erro = null;
      this.carregar();
    },

    recarregar() {
      if (this.ponto) this.load(this.ponto);
    },

    async carregar() {
      if (!this.ponto || !this.ponto.externalId) return;

      this.loading = true;
      try {
        const id = encodeURIComponent(this.ponto.externalId);
        const [resDetalhe, resHorario] = await Promise.all([
          this.urlGet(`/ExternalDevice/${id}`),
          this.urlGet(`/ExternalDevice/${id}/Hourly?dias=${this.dias}`).catch((e) => {
            console.warn("Erro ao carregar série horária:", e);
            return null;
          }),
        ]);

        this.detalhe = resDetalhe.data;
        this.horario = resHorario ? resHorario.data : null;

        // Abre no dia mais recente e na grandeza principal — o que o usuário quer ver.
        if (this.days.length) {
          this.expandedDayIndex = this.days.length - 1;
          this.selectedGrandeza = this.mainGrandeza;
        }
      } catch (e) {
        console.error("Erro ao carregar dispositivo externo:", e);
        this.erro = "Não foi possível carregar os dados desta estação.";
      } finally {
        this.loading = false;
      }
    },

    onDaySelect(index) {
      this.expandedDayIndex = index;
      // Grandeza selecionada pode não existir no dia novo (fontes têm buracos).
      const dia = this.days[index];
      if (dia && (!dia.resumo || !dia.resumo[this.selectedGrandeza])) {
        const chaves = Object.keys(dia.resumo || {});
        this.selectedGrandeza = chaves.length ? chaves[0] : null;
      }
    },

    // Toggle: clicar no card já selecionado fecha o gráfico.
    selectCard(grandeza) {
      this.selectedGrandeza = this.selectedGrandeza === grandeza ? null : grandeza;
    },

    isRowSelected(row) {
      return row.some((c) => c.grandeza === this.selectedGrandeza);
    },

    // Valor exibido no card: leitura atual no dia de hoje, média nos dias anteriores.
    // Fallback para a média se a fonte não reportar aquela grandeza no bloco "atual"
    // (acontece: cada fonte publica um conjunto diferente de campos).
    valorDoCard(grandeza, stat) {
      if (this.isDiaAtual) {
        const atual = this.valoresAtuais[grandeza];
        if (atual != null) return atual;
      }
      return stat ? stat.media : null;
    },

    dayLabel(date) {
      const d = new Date(date + "T00:00:00");
      if (isNaN(d.getTime())) return date;
      return `${WEEK_ABBR[d.getDay()]}. ${d.getDate()}`;
    },

    // Só usado na aba do dia mais recente: mostra a leitura atual, igual ao card do dia
    // (a média divergia do card sempre que o nível subia ou descia ao longo do dia).
    dayMainValue(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      const atual = this.valoresAtuais[this.mainGrandeza];
      if (atual != null) return this.fmt(atual);
      return stat ? this.fmt(stat.media) : "--";
    },

    dayMainRange(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      if (!stat) return "";
      return `${this.fmt(stat.min)}/${this.fmt(stat.max)}`;
    },

    // Usados nas abas de dias já encerrados, onde só mín/máx interessam.
    dayMainMax(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      return stat ? this.fmt(stat.max) : "--";
    },

    dayMainMin(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      return stat ? this.fmt(stat.min) : "--";
    },

    formatDate(iso) {
      if (!iso) return "--";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? "--" : d.toLocaleDateString("pt-BR");
    },

    formatTime(iso) {
      if (!iso) return "--";
      const d = new Date(iso);
      return isNaN(d.getTime())
        ? "--"
        : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },

    fmt(v) {
      if (v == null || v === "") return "--";
      const n = Number(v);
      if (isNaN(n)) return String(v);
      return (Number.isInteger(n) ? n : Number(n.toFixed(1))).toLocaleString("pt-BR");
    },

    humanize(chave) {
      if (!chave) return "";
      const t = String(chave).replace(/_/g, " ").trim();
      return t.charAt(0).toUpperCase() + t.slice(1);
    },
  },
};
</script>

<style scoped>
/* Tokens e classes espelham NewAsideDevice.vue para os dois asides ficarem idênticos. */
.na-label { font-size: 13px; font-weight: 600; color: #333; }

.na-ext-error { text-align: center; padding: 40px 16px; color: #6c757d; }
.na-ext-retry {
  margin-top: 12px; border: 1px solid #006035; background: #fff; color: #006035;
  border-radius: 6px; padding: 6px 14px; font-size: 13px; cursor: pointer;
}
.na-ext-empty { text-align: center; color: #6c757d; font-size: 13px; padding: 24px 0; }

/* Identificação da estação */
.na-ext-source { padding: 0 4px 8px; }
.na-ext-source-box {
  background: #fff; border: 1px solid #ced4da; border-radius: 8px; padding: 8px 12px;
}
.na-ext-source-main { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.na-ext-source-name { font-size: 14px; color: #333; font-weight: 600; }
.na-ext-source-code { font-size: 12px; color: #999; }
.na-ext-source-river { font-size: 12px; color: #666; }

/* Faixa de situação de cheia — dentro do painel do dia, junto aos cards do sensor. */
.na-ext-status { border-radius: 10px; padding: 8px 12px; color: #fff; margin-bottom: 8px; }
.na-ext-status-main { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.na-ext-status-label { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; }
.na-ext-status-trend {
  font-size: 12px; font-weight: 600; opacity: .95; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 4px;
}
/* Alinha a seta SVG à linha de base do texto ao lado ("Subindo"). */
.na-ext-trend-glyph { display: inline-flex; align-items: center; }
.na-ext-status-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 3px;
  min-height: 18px;
}
.na-ext-status-rate { font-size: 12px; opacity: .9; }
.na-ext-status-form { display: flex; align-items: center; gap: 4px; margin-left: auto; }
.na-ext-form-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  line-height: 1;
  /* Mesma anatomia do .external-pin-badge do mapa: disco na cor da tendência, aro branco
     e seta branca. O aro é o que separa o disco da faixa colorida atrás — sem ele, o
     verde de "descendo" encostaria no verde de "normal". */
  border: 1.5px solid #fff;
  color: #fff;
}
/* A leitura atual é a última da sequência — destacá-la evita ler de trás pra frente.
   O realce é um halo externo, que não altera a cor do disco nem o tamanho da bolinha. */
.na-ext-form-dot.is-current {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, .45);
}

.na-section { background: #fff; border-radius: 10px; padding: 12px; margin-bottom: 8px; }

/* Níveis de referência */
.na-ext-refs { padding: 8px 12px; }
.na-ext-refs-label { font-size: 12px; color: #333; margin-bottom: 6px; }
.na-ext-ref-item { display: flex; align-items: center; gap: 8px; padding: 3px 0; font-size: 12px; color: #666; }
.na-ext-ref-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.na-ext-ref-nome { flex: 1 1 auto; }
.na-ext-ref-valor { font-variant-numeric: tabular-nums; color: #333; }
/* Referência já ultrapassada pelo nível atual. */
.na-ext-ref-valor.atingido { font-weight: 700; color: #333; }
/* Faixa em que o rio se encontra agora. */
.na-ext-ref-faixa-atual { font-weight: 700; color: #333; }
.na-ext-ref-faixa-atual .na-ext-ref-dot {
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
}

.na-historico-header {
  display: flex; justify-content: space-between; align-items: center; padding: 8px 4px;
}
.na-historico-title { font-weight: 600; font-size: 14px; color: #333; }

/* Acordeão de dias */
.na-days-container { position: relative; }
.na-days-row {
  display: flex; overflow-x: auto; gap: 3px; padding: 0; position: relative; z-index: 2;
  -ms-overflow-style: none; scrollbar-width: none;
}
.na-days-row::-webkit-scrollbar { display: none; }

.na-day-item {
  flex: 1 1 0; min-width: 0; border: none; border-radius: 8px; background: #F2F4F1;
  text-align: center; padding: 6px 4px; cursor: pointer; transition: background-color .2s;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.na-day-item:hover { background: #e8ebe6; }
.na-day-item.active {
  background-color: #FFFFFF; border: 1px solid #C3D9AD;
  border-bottom-left-radius: 0; border-bottom-right-radius: 0; border-bottom-color: transparent;
  position: relative; z-index: 3; margin-bottom: -1px;
}
.na-day-text { font-size: 11px; font-weight: 600; color: #333; }
.na-day-rain { display: flex; flex-direction: column; align-items: center; line-height: 1; }
.na-day-value { font-size: 16px; font-weight: 700; color: #006035; }
.na-day-unit { font-size: 10px; color: #666; }
.na-day-temp-range { font-size: 10px; color: #666; }

/* Dias encerrados: máx/mín empilhados, com a unidade no rodapé.
   O bloco ocupa toda a altura livre da aba (flex:1) e distribui os três itens com
   space-between: isso espaça máx/mín e joga a unidade para a base, na mesma linha em que
   o dia atual mostra a faixa "12,8/12,8" — sem isso a unidade subia e desalinhava. */
.na-day-minmax {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  line-height: 1.1;
  padding: 1px 0 0;
}
.na-day-minmax-item {
  display: flex;
  align-items: baseline;
  gap: 3px;
}
.na-day-minmax-cap {
  font-size: 8px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: .2px;
}
.na-day-minmax-val {
  font-size: 12px;
  font-weight: 700;
  color: #006035;
  font-variant-numeric: tabular-nums;
}
.na-day-minmax-unit {
  font-size: 10px;
  color: #666;
}

/* Todas as abas com a mesma altura: hoje tem 3 linhas (valor, unidade, faixa) e os dias
   passados também (máx, mín, unidade), mas as alturas de fonte diferem. Sem isto a linha
   de abas fica com dentes. */
.na-day-item {
  justify-content: flex-start;
  min-height: 62px;
}

.na-expanded-content {
  border: 1px solid #C3D9AD; border-radius: 10px; background: #FFFFFF80; padding: 10px;
  position: relative; z-index: 1; margin-top: 0; animation: na-expand .3s ease-out;
}
@keyframes na-expand {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Última leitura */
.na-leitura { padding: 6px 12px; }
.na-leitura-row { display: flex; justify-content: space-between; align-items: center; }
.na-leitura-label { font-weight: 600; font-size: 12px; color: #333; }
.na-leitura-date { font-size: 12px; color: #333; }
.na-leitura-time { font-size: 12px; color: #006035; }

.na-metrics-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }

/* Painel de gráfico — mesmo azul do card selecionado, formando um bloco contínuo. */
.na-chart-table-card {
  background: #EFF3FF; border: 1px solid #BBD1FF; border-radius: 10px;
  padding: 10px; margin-bottom: 8px;
}

.na-report-container { text-align: center; margin-top: 8px; margin-bottom: 8px; }
.na-btn-alert {
  display: block; background-color: #006035; color: #fff; border: none; border-radius: 10px;
  padding: 12px; width: 100%; font-weight: 600; font-size: 14px; text-align: center;
  text-decoration: none; transition: background-color .2s;
}
.na-btn-alert:hover { background-color: #004d29; color: #fff; }

.na-ext-fonte-nota { font-size: 11px; color: #adb5bd; text-align: center; margin: 0; }
</style>
